import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import Icon from '../components/Icon';
import { F1_DRIVERS, driverImgUrl } from '../constants/f1Drivers';
import { getLevelInfo, getGrade } from '../utils/xpUtils';

const PODIUM = [
  { medal: '🥇', border: '#F59E0B', glow: '#F59E0B30', bg: 'linear-gradient(135deg,#F59E0B12 0%,transparent 60%)' },
  { medal: '🥈', border: '#94A3B8', glow: '#94A3B830', bg: 'linear-gradient(135deg,#94A3B812 0%,transparent 60%)' },
  { medal: '🥉', border: '#B45309', glow: '#B4530930', bg: 'linear-gradient(135deg,#B4530912 0%,transparent 60%)' },
];

const PlayerCard = ({ player, rank }) => {
  const { level, progress } = getLevelInfo(player.xp ?? 0);
  const g = getGrade(level);
  const pod = rank <= 3 ? PODIUM[rank - 1] : null;

  const driverObj = (() => {
    if (player.seasonDriver)
      return F1_DRIVERS.find(d => d.code === player.seasonDriver.code || d.name === player.seasonDriver.name) || null;
    if (player.favoriteDriver)
      return F1_DRIVERS.find(d => d.name === player.favoriteDriver.name) || null;
    return null;
  })();

  const accentColor = driverObj?.color || (pod ? pod.border : '#e10600');
  const xpProgress = Math.min(100, Math.round((progress ?? 0) * 100));

  return (
    <Link to={`/joueurs/${player._id}`}
      className="group relative overflow-hidden rounded-2xl border transition-all duration-200 no-underline flex flex-col"
      style={{
        borderColor: pod ? pod.border : `${accentColor}33`,
        background: pod ? pod.bg : `linear-gradient(135deg,${accentColor}0a 0%,#09090b 60%)`,
        boxShadow: pod ? `0 0 24px ${pod.glow}` : undefined,
      }}>

      {/* Top rank stripe */}
      {pod && (
        <div className="h-0.5 w-full" style={{ background: `linear-gradient(90deg,${pod.border} 0%,transparent 70%)` }} />
      )}

      <div className="p-4 flex flex-col gap-3 flex-1">
        {/* Row 1: rank + avatar + identity + score */}
        <div className="flex items-center gap-3">
          {/* Rank */}
          <div className="w-7 text-center shrink-0">
            {rank <= 3
              ? <span className="text-xl">{pod.medal}</span>
              : <span className="text-xs font-black text-zinc-500">#{rank}</span>}
          </div>

          {/* Avatar */}
          <div className="relative shrink-0">
            <div className="w-11 h-11 rounded-full overflow-hidden border-2"
              style={{ borderColor: `${accentColor}66` }}>
              {player.avatar
                ? <img src={player.avatar} alt={player.username} className="w-full h-full object-cover" />
                : <div className="w-full h-full flex items-center justify-center text-base font-black"
                    style={{ background: `${accentColor}22`, color: accentColor }}>
                    {player.username?.[0]?.toUpperCase()}
                  </div>
              }
            </div>
          </div>

          {/* Name + grade */}
          <div className="flex-1 min-w-0">
            <p className="text-white font-black text-sm truncate group-hover:text-f1red transition-colors">{player.username}</p>
            <span className="inline-block text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded mt-0.5"
              style={{ color: g.color, background: g.bg }}>{g.label}</span>
          </div>

          {/* Score */}
          <div className="text-right shrink-0">
            <p className="font-black text-base leading-tight" style={{ color: pod ? pod.border : '#e10600' }}>
              {player.totalScore ?? 0}
            </p>
            <p className="text-zinc-600 text-[9px] uppercase tracking-wider">pts</p>
          </div>
        </div>

        {/* XP bar */}
        <div className="flex items-center gap-2">
          <span className="text-zinc-500 text-[9px] shrink-0">Niv. {level}</span>
          <div className="flex-1 h-1 bg-zinc-800 rounded-full overflow-hidden">
            <div className="h-full rounded-full transition-all"
              style={{ width: `${xpProgress}%`, background: `linear-gradient(90deg,${accentColor},${accentColor}80)` }} />
          </div>
          <span className="text-zinc-600 text-[9px] shrink-0">{(player.xp ?? 0).toLocaleString()} XP</span>
        </div>

        {/* Driver */}
        {driverObj ? (
          <div className="flex items-center gap-2 rounded-xl px-2.5 py-1.5 border"
            style={{ borderColor: `${driverObj.color}25`, background: `${driverObj.color}0c` }}>
            <div className="w-8 h-8 rounded-lg overflow-hidden shrink-0"
              style={{ background: `${driverObj.color}18` }}>
              <img src={driverImgUrl(driverObj.img)} alt={driverObj.name}
                className="w-full h-full object-cover object-top"
                onError={e => { e.target.style.display = 'none'; }} />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-white text-xs font-bold truncate">{driverObj.name}</p>
              <p className="text-[9px] truncate" style={{ color: driverObj.color }}>{driverObj.team}</p>
            </div>
            <span className="text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded shrink-0"
              style={{ color: driverObj.color, background: `${driverObj.color}18` }}>
              {player.seasonDriver ? 'Saison' : 'Favori'}
            </span>
          </div>
        ) : (
          <div className="flex items-center gap-2 rounded-xl px-2.5 py-1.5 border border-zinc-800/60">
            <Icon name="user" size={11} className="text-zinc-700 shrink-0" />
            <p className="text-zinc-700 text-xs">Aucun pilote</p>
          </div>
        )}
      </div>

      {/* Hover overlay */}
      <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"
        style={{ background: `radial-gradient(ellipse at 50% 0%,${accentColor}10 0%,transparent 70%)` }} />
    </Link>
  );
};

const Players = () => {
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    api.get('/leaderboard/players')
      .then(res => setPlayers(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const filtered = players.filter(p =>
    p.username.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="max-w-4xl mx-auto px-4 py-10">
      {/* Header */}
      <div className="mb-8">
        <p className="text-zinc-500 text-xs uppercase tracking-widest font-medium mb-1">Communauté</p>
        <h1 className="text-4xl font-black text-white tracking-tight">
          Les <span className="text-f1red">Pilotes</span>
        </h1>
        <p className="text-zinc-400 mt-2 text-sm">
          {players.length} joueur{players.length !== 1 ? 's' : ''} inscrit{players.length !== 1 ? 's' : ''}
        </p>
      </div>

      {/* Search */}
      <div className="relative mb-8">
        <svg className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
        </svg>
        <input
          type="text"
          placeholder="Rechercher un joueur..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full rounded-xl pl-11 pr-4 py-3 text-sm text-white placeholder-zinc-500 focus:outline-none transition-all"
          style={{
            background: 'rgba(255,255,255,0.05)',
            border: '1px solid rgba(255,255,255,0.10)',
          }}
          onFocus={e => { e.target.style.borderColor = '#e10600'; e.target.style.boxShadow = '0 0 0 3px #e1060018'; }}
          onBlur={e => { e.target.style.borderColor = 'rgba(255,255,255,0.10)'; e.target.style.boxShadow = 'none'; }}
        />
        {search && (
          <button onClick={() => setSearch('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white transition-colors text-lg leading-none">
            ✕
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center h-48 gap-3">
          <div className="w-8 h-8 border-2 border-f1red border-t-transparent rounded-full animate-spin" />
          <p className="text-zinc-500 text-sm">Chargement des pilotes…</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="card text-center py-14">
          <p className="text-zinc-400 text-lg font-bold mb-1">Aucun joueur trouvé</p>
          <p className="text-zinc-600 text-sm">Essaie un autre terme de recherche.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((player) => {
            const rank = players.indexOf(player) + 1;
            return <PlayerCard key={player._id} player={player} rank={rank} />;
          })}
        </div>
      )}
    </div>
  );
};

export default Players;
